import React, { useState, useEffect, useMemo, useCallback } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner } from "../../components/ui";
import { derive } from "../admin/recordDerive";

const SENDBACK = new Set(["Modification Needed", "More Info Needed"]);

/**
 * FTR (First Time Right) / STR / TTR.
 * Per approved record: count the send-backs (Modification / More Info) in its journey.
 *   0 send-backs -> FTR, 1 -> STR, 2+ -> TTR.
 * The record is credited to whoever gave the Approval. Only approved records are counted.
 */
export function FTRReport() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [selUsers, setSelUsers] = useState(new Set());
  const [usersOpen, setUsersOpen] = useState(false);
  const [payor, setPayor] = useState("");
  const [clinic, setClinic] = useState("");
  const [group, setGroup] = useState("");

  const load = useCallback(async () => {
    try { setRows(await api.workedLight()); setErr(""); }
    catch (e) { setErr(e.message); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // one classified item per approved record
  const items = useMemo(() => {
    const out = [];
    (rows || []).forEach((r) => {
      let hist = [];
      try { hist = r.workHistory ? JSON.parse(r.workHistory) : []; } catch { hist = []; }
      if (!Array.isArray(hist) || !hist.length) return;
      // must have reached Approved
      let approvalIdx = -1;
      for (let i = hist.length - 1; i >= 0; i--) { if ((hist[i].status || "").toLowerCase() === "approved") { approvalIdx = i; break; } }
      if (approvalIdx === -1) return;   // only approved records count

      // credit the SUBMITTER — the person who did the original Submission round
      const subRound = hist.find((h) => h.stage === "SUBMISSION");
      const submitter = (subRound && subRound.by) || hist[0].by || "Unknown";

      // count send-backs (Modification / More Info) across the whole journey up to approval
      let reworks = 0;
      for (let i = 0; i <= approvalIdx; i++) { if (SENDBACK.has(hist[i].inbox)) reworks += 1; }
      const bucket = reworks === 0 ? "ftr" : reworks === 1 ? "str" : "ttr";
      const d = derive(r) || {};
      out.push({
        user: submitter,
        payor: d.insurance || "—", clinic: d.clinic || "—", group: d.location || "—",
        bucket,
      });
    });
    return out;
  }, [rows]);

  const allUsers = useMemo(() => Array.from(new Set(items.map((i) => i.user))).sort(), [items]);
  const allPayors = useMemo(() => Array.from(new Set(items.map((i) => i.payor).filter((x) => x && x !== "—"))).sort(), [items]);
  const allClinics = useMemo(() => Array.from(new Set(items.map((i) => i.clinic).filter((x) => x && x !== "—"))).sort(), [items]);
  const allGroups = useMemo(() => Array.from(new Set(items.map((i) => i.group).filter((x) => x && x !== "—"))).sort(), [items]);

  const report = useMemo(() => {
    const byUser = new Map();
    items.forEach((it) => {
      if (selUsers.size && !selUsers.has(it.user)) return;
      if (payor && it.payor !== payor) return;
      if (clinic && it.clinic !== clinic) return;
      if (group && it.group !== group) return;
      if (!byUser.has(it.user)) byUser.set(it.user, { ftr: 0, str: 0, ttr: 0 });
      byUser.get(it.user)[it.bucket] += 1;
    });
    return Array.from(byUser.entries()).map(([user, b]) => {
      const total = b.ftr + b.str + b.ttr;
      return { user, ...b, total, ftrPct: total ? (b.ftr / total) * 100 : 0, strPct: total ? (b.str / total) * 100 : 0, ttrPct: total ? (b.ttr / total) * 100 : 0 };
    }).sort((a, b) => b.ftrPct - a.ftrPct || b.total - a.total || a.user.localeCompare(b.user));
  }, [items, selUsers, payor, clinic, group]);

  const totals = useMemo(() => {
    const t = { ftr: 0, str: 0, ttr: 0 };
    report.forEach((r) => { t.ftr += r.ftr; t.str += r.str; t.ttr += r.ttr; });
    const total = t.ftr + t.str + t.ttr;
    return { ...t, total, ftrPct: total ? (t.ftr / total) * 100 : 0, strPct: total ? (t.str / total) * 100 : 0, ttrPct: total ? (t.ttr / total) * 100 : 0 };
  }, [report]);

  const toggleUser = (u) => setSelUsers((p) => { const n = new Set(p); n.has(u) ? n.delete(u) : n.add(u); return n; });
  const clearAll = () => { setSelUsers(new Set()); setPayor(""); setClinic(""); setGroup(""); };
  const usersLabel = selUsers.size === 0 ? "All users" : `${selUsers.size} user${selUsers.size > 1 ? "s" : ""} selected`;

  if (rows === null) return <div className="empty"><Spinner /></div>;

  const cell = (n) => (n ? n : <span className="alloc-zero">·</span>);
  const pct = (v) => `${v.toFixed(1)}%`;

  return (
    <div className="prod">
      <div className="prod-filters">
        <div className="prod-field">
          <span className="prod-flabel">Users</span>
          <div className="prod-userpick">
            <button className="inp prod-userbtn" onClick={() => setUsersOpen((o) => !o)}><Icon.users width="15" height="15" /> {usersLabel} <Icon.chevron width="14" height="14" /></button>
            {usersOpen && (
              <div className="prod-userpop">
                <label className="prod-user-row prod-user-all"><input type="checkbox" checked={selUsers.size === 0} onChange={() => setSelUsers(new Set())} /> All users</label>
                <div className="prod-user-list">
                  {allUsers.map((u) => <label key={u} className="prod-user-row"><input type="checkbox" checked={selUsers.has(u)} onChange={() => toggleUser(u)} /> {u}</label>)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="prod-field">
          <span className="prod-flabel">Payor</span>
          <select className="inp" value={payor} onChange={(e) => setPayor(e.target.value)}>
            <option value="">All payors</option>
            {allPayors.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="prod-field">
          <span className="prod-flabel">Clinic location</span>
          <select className="inp" value={clinic} onChange={(e) => setClinic(e.target.value)}>
            <option value="">All locations</option>
            {allClinics.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="prod-field">
          <span className="prod-flabel">Group</span>
          <select className="inp" value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All groups</option>
            {(allGroups.length ? allGroups : ["LACN", "CCCMG"]).map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <button className="link-btn" onClick={clearAll} style={{ alignSelf: "flex-end", marginBottom: 6 }}>Clear</button>
      </div>

      {err && <div className="import-err"><Icon.x width="15" height="15" /><span>{err}</span></div>}

      <div className="card prod-card"><div className="table-wrap">
        <table className="tbl prod-tbl ftr-tbl">
          <thead>
            <tr>
              <th className="prod-usercol">User</th>
              <th style={{ textAlign: "center" }} className="sec-work">FTR</th>
              <th style={{ textAlign: "center" }}>FTR %</th>
              <th style={{ textAlign: "center" }} className="sec-status">STR</th>
              <th style={{ textAlign: "center" }}>STR %</th>
              <th style={{ textAlign: "center" }} className="sec-issue">TTR</th>
              <th style={{ textAlign: "center" }}>TTR %</th>
              <th style={{ textAlign: "center" }}>Approved total</th>
            </tr>
          </thead>
          <tbody>
            {report.length > 1 && (
              <tr className="prod-total prod-total-top">
                <td className="prod-usercol">All ({report.length})</td>
                <td className="sec-work" style={{ textAlign: "center" }}>{totals.ftr}</td>
                <td style={{ textAlign: "center", color: "#047857", fontWeight: 800 }}>{pct(totals.ftrPct)}</td>
                <td className="sec-status" style={{ textAlign: "center" }}>{totals.str}</td>
                <td style={{ textAlign: "center", color: "#b45309", fontWeight: 800 }}>{pct(totals.strPct)}</td>
                <td className="sec-issue" style={{ textAlign: "center" }}>{totals.ttr}</td>
                <td style={{ textAlign: "center", color: "#b91c1c", fontWeight: 800 }}>{pct(totals.ttrPct)}</td>
                <td style={{ textAlign: "center", fontWeight: 800 }}>{totals.total}</td>
              </tr>
            )}
            {report.length === 0 ? (
              <tr><td colSpan="8" className="prod-empty">No approved records for these filters.</td></tr>
            ) : report.map((r) => (
              <tr key={r.user}>
                <td className="prod-usercol"><b>{r.user}</b></td>
                <td className="sec-work" style={{ textAlign: "center" }}>{cell(r.ftr)}</td>
                <td style={{ textAlign: "center", color: "#047857", fontWeight: 700 }}>{pct(r.ftrPct)}</td>
                <td className="sec-status" style={{ textAlign: "center" }}>{cell(r.str)}</td>
                <td style={{ textAlign: "center", color: "#b45309", fontWeight: 700 }}>{pct(r.strPct)}</td>
                <td className="sec-issue" style={{ textAlign: "center" }}>{cell(r.ttr)}</td>
                <td style={{ textAlign: "center", color: "#b91c1c", fontWeight: 700 }}>{pct(r.ttrPct)}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>
      <p className="prod-note"><b>FTR</b> = approved with no send-backs · <b>STR</b> = approved after 1 send-back · <b>TTR</b> = 2+ send-backs. Credited to the submitter (who did the original submission). Second/third time usually reflects a client / clinic issue.</p>
    </div>
  );
}